import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from "axios";
import { APIURL } from "../../Global";
import Pending from './Pending';
import ApprovedRejected from './ApprovedRejected';

const VISITOR_TYPES = {
    RELATIVE: 'relative',
    FRIEND: 'friend',
    GENERAL: 'general',
    ORGANISATION: 'organisation'
};

const STATUS_TYPES = {
    APPROVED: 'approved',
    REJECTED: 'rejected'
};

function Visitor() {
    const [selectedCategory, setSelectedCategory] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredVisitors, setFilteredVisitors] = useState([]);
    const [allVisitors, setAllVisitors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const role = sessionStorage.getItem("usertype");

    const isHospitalRole = useMemo(() =>
        role === 'hospital_bdo' || role === 'hospital_pro',
        [role]
    );

    useEffect(() => {
        window.scrollTo(0, 0);
        setSelectedCategory(isHospitalRole ? STATUS_TYPES.APPROVED : VISITOR_TYPES.RELATIVE);
    }, [isHospitalRole]);

    const fetchVisitors = useCallback(async () => {
        if (role === 'hospital_bdo' || role !== 'hospital_pro') return;

        setLoading(true);
        setError("");

        try {
            const tokenString = sessionStorage.getItem("usertoken");
            const token = tokenString?.replace(/["]+/g, "");

            if (!token) {
                throw new Error("No authentication token found");
            }

            const response = await axios.get(
                `${APIURL}/api/v1/service-provider/visitor-list/`,
                {
                    headers: { Authorization: `Token ${token}` }
                }
            );

            if (response.status === 200) {
                const pendingVisitors = response.data.visitors.filter(
                    visitor => visitor.status === "pending"
                );
                setAllVisitors(pendingVisitors);
            }
        } catch (err) {
            setError("Failed to fetch visitors data");
            console.error("Error fetching visitors:", err);
        } finally {
            setLoading(false);
        }
    }, [role]);

    useEffect(() => {
        fetchVisitors();
    }, [fetchVisitors]);

    // Enhanced search functionality
    const performSearch = useCallback((term) => {
        if (!term.trim()) {
            setFilteredVisitors([]);
            return;
        }

        const normalizedTerm = term.toLowerCase().trim();

        const searchResults = allVisitors
            .filter(visitor => {
                const fullName = `${visitor.first_name} ${visitor.last_name}`.toLowerCase();
                const visitorId = visitor.id.toString().toLowerCase();

                return fullName.includes(normalizedTerm) ||
                    visitorId.includes(normalizedTerm) ||
                    visitor.first_name?.toLowerCase().includes(normalizedTerm) ||
                    visitor.last_name?.toLowerCase().includes(normalizedTerm);
            })
            .sort((a, b) => {
                const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
                const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
                return nameA.localeCompare(nameB);
            });

        if (searchResults.length > 0) {
            const firstResultType = searchResults[0].visitor_type;
            const filteredByType = searchResults.filter(
                visitor => visitor.visitor_type === firstResultType
            );

            setFilteredVisitors(filteredByType);
            setSelectedCategory(firstResultType);
        } else {
            setFilteredVisitors([]);
            setSelectedCategory("");
        }
    }, [allVisitors]);

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (value.trim()) {
            performSearch(value);
        } else {
            setFilteredVisitors([]);
            setSelectedCategory(isHospitalRole ? STATUS_TYPES.APPROVED : VISITOR_TYPES.RELATIVE);
        }
    }, [performSearch, isHospitalRole]);

    const handleCategorySelect = useCallback((category) => {
        setFilteredVisitors([]);
        setSearchTerm("");
        setSelectedCategory(category);
    }, []);

    const renderCategoryButtons = () => (
        <>
            {Object.entries(VISITOR_TYPES).map(([key, value]) => (
                <button
                    key={key}
                    className="deathreportbutton"
                    onClick={() => handleCategorySelect(value)}
                    style={{
                        backgroundColor: selectedCategory === value ? "#6D4482" : "#F8F8F8",
                        color: selectedCategory === value ? "white" : "black"
                    }}
                >
                    <i
                        className="fa fa-file"
                        aria-hidden="true"
                        style={{ color: selectedCategory === value ? "white" : "#6D4482" }}
                    />
                    &nbsp;&nbsp;{key.charAt(0) + key.slice(1).toLowerCase()}
                    {value === VISITOR_TYPES.GENERAL ? ' category' : 's'}
                </button>
            ))}
        </>
    );

    const renderStatusButtons = () => (
        <>
            {Object.entries(STATUS_TYPES).map(([key, value]) => (
                <button
                    key={key}
                    className="deathreportbutton"
                    style={{
                        backgroundColor: selectedCategory === value ? "#6D4482" : "#F8F8F8",
                        color: selectedCategory === value ? "white" : "black"
                    }}
                    onClick={() => handleCategorySelect(value)}
                >
                    <i
                        className="fa fa-file"
                        aria-hidden="true"
                        style={{ color: selectedCategory === value ? "white" : "#6D4482" }}
                    />
                    &nbsp;&nbsp;{key.charAt(0) + key.slice(1).toLowerCase()}
                </button>
            ))}
        </>
    );

    const renderMainContent = () => {
        if (loading) {
            return <div style={{ marginTop: "10%", color: "#6D4482" }}>Loading...</div>;
        }

        if (error) {
            return <div style={{ marginTop: "10%", color: "red" }}>{error}</div>;
        }

        const isStatusCategory = selectedCategory === STATUS_TYPES.APPROVED ||
            selectedCategory === STATUS_TYPES.REJECTED;
        const isPendingCategory = !isStatusCategory && selectedCategory;

        if (isStatusCategory) {
            return <ApprovedRejected status={selectedCategory} />;
        }

        if (isPendingCategory) {
            return <Pending type={selectedCategory} visitor={filteredVisitors} />;
        }

        return (
            <div style={{ marginTop: "10%", color: "red" }}>
                {searchTerm ? "No matching visitors found!" : "No Requests Pending!!"}
            </div>
        );
    };

    return (
        <div style={{ paddingLeft: "1rem", paddingRight: "1.5rem" }}>
            <div style={{display:'flex'}}>
                <div className="col-lg-2 col-md-2">
                    <h4
                        style={{
                            color: "#245693",
                            marginTop: "8%",
                            fontFamily: "inter",
                            fontWeight: "700",
                            textAlign:'center'
                        }}
                    >
                        Visitors Report
                    </h4>

                    {!isHospitalRole &&
                        <div className="leftdivdeathrepo" style={{ padding: '8px' }}>
                            <div className="searchboxdeathrepo">
                                <input
                                    placeholder="Search Name or ID"
                                    type="text"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    style={{
                                        marginLeft: '10px',
                                        marginTop: "3px",
                                        border: "0",
                                        outline: "none",
                                        width: "85%"
                                    }}
                                />
                            </div>
                            <div style={{ marginTop: "10px" }}>
                                {renderCategoryButtons()}
                            </div>

                        </div>}

                    <div
                        className="leftdivdeathrepo"
                        style={{
                            backgroundColor: "#6d448224",
                            padding: '8px',
                            marginTop: "1%",
                            marginBottom: "1%"
                        }}
                    >
                        {renderStatusButtons()}
                    </div>
                </div>

                <div className="col-lg-10 col-md-10">
                    {renderMainContent()}
                </div>
            </div>
        </div>
    );
}

export default Visitor;